import { jsxs, jsx } from 'react/jsx-runtime';
import React, { forwardRef } from 'react';
import { defaultBaseline, defaultFonts, darken, lighten, clsx, safeRest, KEYS } from '@wds/utils';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/** Resolves the correct color */
var getMarkedColor = function getMarkedColor(color, palette) {
  if (!color || typeof color !== 'string') {
    return palette.primary.main;
  }
  if (color.startsWith('data-')) {
    return palette.data[color.replace('data-', '')];
  }
  return palette[color].main;
};
var useStyles$1 = createMemoStyles(function (theme) {
  var palette = theme.palette,
    border = theme.border,
    spacing = theme.spacing,
    motion = theme.motion,
    type = theme.type;
  var list = {
    addonIconColor: palette.background.dark,
    affordanceIconColor: palette.secondary.main,
    itemMinHeight: 48,
    labelFontSize: type.fontSize.bodySm,
    markWidth: 4
  };
  var listStyles = {
    wrenchListRow: {
      borderBottom: border.main,
      '&:last-child': {
        borderBottom: 'none'
      },
      'a&': {
        color: palette.text.dark,
        '&:hover, &:focus': {
          textDecoration: 'none'
        }
      }
    },
    wrenchListItem: __assign(__assign(__assign({}, defaultBaseline), defaultFonts(theme)), {
      fontWeight: function (_a) {
        var menu = _a.menu;
        return menu ? type.fontWeight.regular : type.fontWeight.medium;
      },
      display: 'flex',
      background: function (_a) {
        var transparent = _a.transparent;
        return transparent ? 'none' : palette.background.light;
      },
      position: 'relative',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: list.itemMinHeight,
      'a&': {
        textDecoration: 'none'
      },
      '&:hover, &:focus, &:active': {
        cursor: function (_a) {
          var accordion = _a.accordion;
          return accordion ? 'pointer' : null;
        }
      },
      // same specificity as parent List rules
      '[class^="wrenchList-"] &': {
        transition: function (_a) {
          var interactive = _a.interactive,
            isFocused = _a.isFocused;
          if (interactive && isFocused) return 'background 125ms linear';
          return 'background 180ms linear';
        },
        background: function (_a) {
          var upgrade = _a.upgrade,
            interactive = _a.interactive,
            isFocused = _a.isFocused;
          if (upgrade) return lighten(0.42, palette.upgrade.main);
          if (interactive && isFocused) return palette.background.accent;
          return null;
        },
        cursor: function (_a) {
          var interactive = _a.interactive,
            isFocused = _a.isFocused;
          return interactive && isFocused ? 'pointer' : null;
        },
        '&:hover, &:focus': {
          background: function (_a) {
            var upgrade = _a.upgrade,
              interactive = _a.interactive;
            if (interactive && upgrade) return lighten(0.35, palette.upgrade.main);
            if (interactive) return palette.background.accent;
            return null;
          },
          transition: function (_a) {
            var interactive = _a.interactive;
            return interactive ? 'background 125ms linear' : null;
          },
          cursor: function (_a) {
            var interactive = _a.interactive;
            return interactive ? 'pointer' : null;
          }
        },
        '&:active': {
          transition: function (_a) {
            var interactive = _a.interactive;
            return interactive ? 'background 125ms linear' : null;
          },
          background: function (_a) {
            var interactive = _a.interactive;
            return interactive ? darken(0.05, palette.background.accent) : null;
          }
        },
        opacity: function (_a) {
          var disabled = _a.disabled;
          return disabled ? 0.5 : null;
        },
        pointerEvents: function (_a) {
          var disabled = _a.disabled;
          return disabled ? 'none' : null;
        },
        '&:before': {
          content: function (_a) {
            var color = _a.color,
              marked = _a.marked;
            return color || marked ? '""' : null;
          },
          position: function (_a) {
            var color = _a.color,
              marked = _a.marked;
            return color || marked ? 'absolute' : null;
          },
          left: function (_a) {
            var color = _a.color,
              marked = _a.marked;
            return color || marked ? 0 : null;
          },
          top: function (_a) {
            var color = _a.color,
              marked = _a.marked;
            return color || marked ? 0 : null;
          },
          bottom: function (_a) {
            var color = _a.color,
              marked = _a.marked;
            return color || marked ? 0 : null;
          },
          width: function (_a) {
            var color = _a.color,
              marked = _a.marked;
            return color || marked ? list.markWidth : null;
          },
          background: function (_a) {
            var color = _a.color,
              marked = _a.marked;
            return color || marked ? getMarkedColor(color, palette) : null;
          }
        }
      }
    }),
    wrenchListAddon: function () {
      return {
        padding: [0, spacing[4], 0, spacing[5]],
        color: list.addonIconColor,
        display: 'flex',
        alignItems: 'center',
        '> [class^="wrenchIcons-"]': {
          marginLeft: spacing[2]
        }
      };
    },
    /** Additional styles when addon is on the right */
    wrenchListAddonOnRight: function () {
      return {
        paddingLeft: 0,
        paddingRight: spacing[6],
        color: palette.text.lightMuted
      };
    },
    wrenchListAffordance: {
      padding: function (_a) {
        var addOnLeft = _a.addOnLeft;
        return addOnLeft ? [[0, 0, 0, 0]] : [[0, spacing[6], 0, 0]];
      },
      color: list.affordanceIconColor,
      display: 'flex',
      alignItems: 'center',
      '& > *': {
        transition: "transform 150ms ".concat(motion.curve.ease)
      }
    },
    wrenchListLabel: {
      flexGrow: 1,
      padding: function (_a) {
        var addOnLeft = _a.addOnLeft;
        return addOnLeft ? [[0, spacing[5], 0, 0]] : [[0, spacing[5]]];
      },
      fontSize: list.labelFontSize,
      lineHeight: 1.25,
      minWidth: 0,
      'a &': {
        color: palette.text.dark
      }
    },
    wrenchPaneHide: {
      border: function (_a) {
        var expanded = _a.expanded;
        return expanded ? null : '0px';
      },
      clip: function (_a) {
        var expanded = _a.expanded;
        return expanded ? null : 'rect(1px, 1px, 1px, 1px)';
      },
      height: function (_a) {
        var expanded = _a.expanded;
        return expanded ? null : '1px';
      },
      margin: function (_a) {
        var expanded = _a.expanded;
        return expanded ? null : '-1px';
      },
      overflow: function (_a) {
        var expanded = _a.expanded;
        return expanded ? null : 'hidden';
      },
      padding: function (_a) {
        var expanded = _a.expanded;
        return expanded ? null : 0;
      },
      position: function (_a) {
        var expanded = _a.expanded;
        return expanded ? null : 'absolute';
      },
      width: function (_a) {
        var expanded = _a.expanded;
        return expanded ? null : '1px';
      },
      whiteSpace: function (_a) {
        var expanded = _a.expanded;
        return expanded ? null : 'nowrap';
      },
      wordWrap: function (_a) {
        var expanded = _a.expanded;
        return expanded ? null : 'normal';
      }
    }
  };
  return listStyles;
});

var ListItem = /*#__PURE__*/forwardRef(function (_a, ref) {
  var addOnLeft = _a.addOnLeft,
    addOnLeftIsInteractive = _a.addOnLeftIsInteractive,
    addOnRight = _a.addOnRight,
    addOnRightIsInteractive = _a.addOnRightIsInteractive,
    affordance = _a.affordance,
    children = _a.children,
    color = _a.color,
    component = _a.component,
    _b = _a.disabled,
    disabled = _b === void 0 ? false : _b,
    id = _a.id,
    index = _a.index,
    _c = _a.isFocused,
    isFocused = _c === void 0 ? false : _c,
    _d = _a.interactive,
    interactive = _d === void 0 ? false : _d,
    _e = _a.marked,
    marked = _e === void 0 ? false : _e,
    _f = _a.menu,
    menu = _f === void 0 ? false : _f,
    // eslint-disable-next-line no-script-url
    _g = _a.navigationTarget,
    // eslint-disable-next-line no-script-url
    navigationTarget = _g === void 0 ? 'javascript:void(0)' : _g,
    target = _a.target,
    onClick = _a.onClick,
    _h = _a.upgrade,
    upgrade = _h === void 0 ? false : _h,
    _j = _a.isNavigable,
    isNavigable = _j === void 0 ? false : _j,
    transparent = _a.transparent,
    other = __rest(_a, ["addOnLeft", "addOnLeftIsInteractive", "addOnRight", "addOnRightIsInteractive", "affordance", "children", "color", "component", "disabled", "id", "index", "isFocused", "interactive", "marked", "menu", "navigationTarget", "target", "onClick", "upgrade", "isNavigable", "transparent"]);
  // no reason to expose these in props
  var _k = other.accordion,
    accordion = _k === void 0 ? false : _k,
    expanded = other.expanded,
    paneComponent = other.paneComponent,
    ariaOwns = other["aria-owns"],
    rest = __rest(other, ["accordion", "expanded", "paneComponent", 'aria-owns']);
  var _l = useStyles$1({
      accordion: accordion,
      color: color,
      marked: marked,
      menu: menu,
      disabled: disabled,
      upgrade: upgrade,
      isFocused: isFocused,
      interactive: interactive,
      addOnLeft: addOnLeft,
      expanded: expanded,
      transparent: transparent
    }),
    wrenchListRow = _l.wrenchListRow,
    wrenchListItem = _l.wrenchListItem,
    wrenchListLabel = _l.wrenchListLabel,
    wrenchListAddon = _l.wrenchListAddon,
    wrenchListAddonOnRight = _l.wrenchListAddonOnRight,
    wrenchListAffordance = _l.wrenchListAffordance;
  var addOnClassNames = clsx(wrenchListAddon, wrenchListAddonOnRight);
  var href = isNavigable ? navigationTarget : undefined;
  var ListItemWrapper = isNavigable ? component !== null && component !== void 0 ? component : 'a' : 'div';
  var interactiveProps = interactive ? {
    tabIndex: disabled ? '-1' : 0,
    role: 'button'
  } : {};
  var onHandleKeyDown = function onHandleKeyDown(e) {
    if (onClick && (e.key === KEYS.ENTER || e.key === KEYS.SPACE)) {
      onClick(e);
    }
  };
  var onHandleAddOnClick = function onHandleAddOnClick(e) {
    if (addOnLeft && addOnLeftIsInteractive || addOnRight && addOnRightIsInteractive) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  return jsxs("li", {
    className: wrenchListRow,
    "aria-owns": ariaOwns,
    children: [jsxs(ListItemWrapper, __assign({
      disabled: disabled,
      className: wrenchListItem,
      onClick: function (e) {
        return onClick === null || onClick === void 0 ? void 0 : onClick(e);
      },
      onKeyDown: onHandleKeyDown,
      href: href,
      target: isNavigable && target ? target : null,
      id: id,
      "data-index": index,
      ref: ref
    }, interactiveProps, safeRest(rest), {
      children: [addOnLeft && jsx("div", {
        onClick: onHandleAddOnClick,
        className: wrenchListAddon,
        children: addOnLeft
      }), jsx("span", {
        className: wrenchListLabel,
        children: children
      }), addOnRight && jsx("div", {
        onClick: onHandleAddOnClick,
        className: addOnClassNames,
        children: addOnRight
      }), affordance && jsx("span", {
        className: wrenchListAffordance,
        children: affordance
      })]
    })), typeof expanded !== 'undefined' && paneComponent]
  });
});
// The displayName is needed for component's Accordion and perhaps other consumers in the future
// https://reactjs.org/docs/forwarding-refs.html#displaying-a-custom-name-in-devtools
ListItem.displayName = 'ListItem';

var useStyles = createMemoStyles(function (theme) {
  var spacing = theme.spacing,
    type = theme.type;
  var list = {
    itemDenseMinHeight: 40
  };
  var listStyles = {
    wrenchList: __assign(__assign({}, defaultBaseline), {
      display: 'block',
      '& [class^="wrenchListAddon-"]': {
        paddingLeft: function (_a) {
          var dense = _a.dense;
          return dense ? [spacing[4]] : undefined;
        },
        '& [class^="wrenchIcons-"]': {
          paddingLeft: function (_a) {
            var dense = _a.dense;
            return dense ? 0 : undefined;
          }
        }
      },
      // second className so it needs `*- not ^=` selector
      '& [class*="wrenchListAddonOnRight-"]': {
        paddingRight: function (_a) {
          var dense = _a.dense;
          return dense ? [spacing[4]] : undefined;
        }
      },
      '& [class^="wrenchListLabel-"]': {
        fontWeight: function (_a) {
          var dense = _a.dense;
          return dense ? type.fontWeight.regular : undefined;
        },
        margin: function (_a) {
          var dense = _a.dense;
          return dense ? [[spacing[3] + 1, 0]] : undefined;
        }
      },
      '& [class^="wrenchListItem-"]': {
        minHeight: function (_a) {
          var dense = _a.dense;
          return dense ? list.itemDenseMinHeight : undefined;
        },
        // this will make it 42.25px (current minimum default)
        borderBottom: function (_a) {
          var dense = _a.dense;
          return dense ? 'none' : undefined;
        }
      },
      '& [class^="wrenchListRow-"]': {
        borderBottom: function (_a) {
          var noRules = _a.noRules;
          return noRules ? 'none' : undefined;
        }
      }
    })
  };
  return listStyles;
});

var List = /*#__PURE__*/forwardRef(function (_a, ref) {
  var _b = _a.dense,
    dense = _b === void 0 ? false : _b,
    _c = _a.interactive,
    interactive = _c === void 0 ? false : _c,
    _d = _a.noRules,
    noRules = _d === void 0 ? false : _d,
    _e = _a.transparent,
    transparent = _e === void 0 ? false : _e,
    children = _a.children,
    role = _a.role,
    id = _a.id;
  var wrenchList = useStyles({
    dense: dense,
    interactive: interactive,
    noRules: noRules,
    transparent: transparent
  }).wrenchList;
  var newChildren = React.Children.map(children, function (child, index) {
    return /*#__PURE__*/React.isValidElement(child) && /*#__PURE__*/React.cloneElement(child, {
      interactive: interactive,
      index: index,
      transparent: transparent
    });
  });
  return jsx("ul", {
    ref: ref,
    className: wrenchList,
    role: role,
    id: id,
    children: newChildren
  });
});
List.displayName = 'List';
List.Item = ListItem;

export { List };
